import React from 'react';
import { useTranslation } from 'react-i18next';
import type { SingleValue } from 'react-select';

import type { IdPriceListDto } from '@/services/ws-orders/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Filter } from '@/js/libs/ui/src/V2';

import { useMarkedOptionLabel } from 'src/hooks/useMarkedOptionLabel';
import type { Option } from 'src/types';
import { usePriceListOptions } from '../hooks/usePriceListOptions';

interface PriceListFilterProps {
  value: Option<IdPriceListDto> | null;
  onChange: (value: SingleValue<Option<IdPriceListDto>>) => void;
  label?: string;
  includeInactive?: boolean;
}

export const PriceListFilter = React.memo(function PriceListFilter({
  value,
  onChange,
  label,
  includeInactive = true,
}: PriceListFilterProps) {
  const [t] = useTranslation(['common']);
  const { options, pending: loading } = usePriceListOptions(includeInactive);
  const markedOptionLabel = useMarkedOptionLabel({ t });

  const handleChange = useCallbackRef((newValue: SingleValue<Option<IdPriceListDto>>) => {
    onChange(newValue);
  });

  return (
    <Filter<IdPriceListDto>
      loading={loading}
      label={label || t('common:PRICE_LIST')}
      selected={value}
      onChange={handleChange}
      options={options}
      formatOptionLabel={markedOptionLabel}
      dataTestId='price-list-dropdown'
    />
  );
});
